import { Injectable, inject } from '@angular/core';
import { DonneesInstallation, Installation as InstallationService, LigneDonnees, ModeAffichageGraphique } from '../../Services/installation';
import { ChartPoint } from './installation-chart-puissance';

export interface ChartLabels {
  production: string;
  ensoleillement: string;
  uniteProduction?: string;
  uniteEnsoleillement?: string;
}

export interface ChartDonnees {
  data: ChartPoint[];
  labels: ChartLabels;
}

@Injectable({ providedIn: 'root' })
export class InstallationChartService {
  private installationService = inject(InstallationService);

  async chargerDonnees(installationId: number, pm: unknown, mode: ModeAffichageGraphique = 'jour', fin?: Date): Promise<ChartDonnees> {
    const pmNonFiltres = this.extraireListePm(pm);

    const typeRefProduction = (mode == 'jour')? 'ref_puissance' : 'ref_production';
    const refProduction = this.rechercherPm(pmNonFiltres, installationId, typeRefProduction);
    const refEnso = this.rechercherPm(pmNonFiltres, installationId, 'ref_ensoleillement');

    const codeProduction = String((refProduction as Record<string, unknown> | undefined)?.['nom'] ?? '');
    const codeEnsoleillement = String((refEnso as Record<string, unknown> | undefined)?.['nom'] ?? '');
    const codes = [codeProduction, codeEnsoleillement].filter(code => code.length > 0);

    const labels: ChartLabels = {
      production: codeProduction || 'Production',
      ensoleillement: codeEnsoleillement || 'Ensoleillement',
    };

    if (codes.length === 0) {
      return { data: [], labels };
    }

    const data = await this.installationService.getInstallationData(installationId, codes, mode, fin);
    const unites = this.extraireUnites(data);
    return {
      data: this.construireDonneesGraphique(data, codeProduction, codeEnsoleillement),
      labels: {
        ...labels,
        uniteProduction: unites[codeProduction],
        uniteEnsoleillement: unites[codeEnsoleillement],
      },
    };
  }

  /** Extrait `{ code: unité }` depuis `reponse.sources` (ex. `{ MR1: { unite: 'Wh' } }`). */
  private extraireUnites(data: DonneesInstallation | null): Record<string, string> {
    if (!data || Array.isArray(data) || !data.sources) return {};

    const unites: Record<string, string> = {};
    for (const [code, info] of Object.entries(data.sources)) {
      if (typeof info?.unite === 'string') unites[code] = info.unite;
    }
    return unites;
  }

  private extraireListePm(pm: unknown): Record<string, unknown>[] {
    if (!pm || typeof pm !== 'object') return [];
    const candidat = pm as Record<string, unknown>;
    const pointMesures = candidat['pointMesures'];
    if (!Array.isArray(pointMesures)) return [];
    return pointMesures.filter((element): element is Record<string, unknown> =>
      element != null && typeof element === 'object'
    );
  }

  private rechercherPm(
    pmNonFiltres: Record<string, unknown>[],
    idStructureRecherche: number,
    typeReferentRecherche: string,
  ): Record<string, unknown> | undefined {
    return pmNonFiltres.find((element) => {
      return Number(element['idStructure']) === idStructureRecherche
        && String(element['ref'] ?? '') === typeReferentRecherche
        && (element['special'] ?? null) === null;
    });
  }

  private construireDonneesGraphique(
    data: DonneesInstallation | null,
    codeProduction: string,
    codeEnsoleillement: string,
  ): ChartPoint[] {
    const lignes = this.extraireLignesData(data);
    const pointsParDate = new Map<number, ChartPoint>();

    for (const ligne of lignes) {
      const date = this.extraireTimestamp(ligne);
      if (date == null) continue;

      const point = pointsParDate.get(date) ?? { date, production: 0, ensoleillement: 0 };
      const codeLigne = String(ligne.code ?? ligne.nom ?? '');

      if (codeLigne === codeProduction) {
        point.production = this.extraireValeurNumerique(ligne);
      } else if (codeLigne === codeEnsoleillement) {
        point.ensoleillement = this.extraireValeurNumerique(ligne);
      } else if (!codeLigne) {
        point.production = this.extraireValeurDepuisCle(ligne, codeProduction, point.production);
        point.ensoleillement = this.extraireValeurDepuisCle(ligne, codeEnsoleillement, point.ensoleillement);
      }

      pointsParDate.set(date, point);
    }

    return [...pointsParDate.values()].sort((a, b) => a.date - b.date);
  }

  private extraireLignesData(data: DonneesInstallation | null): LigneDonnees[] {
    if (Array.isArray(data)) return data;
    return data?.datas ?? [];
  }

  private extraireTimestamp(ligne: LigneDonnees): number | null {
    for (const candidat of [ligne.date, ligne['datetime'], ligne['horodate'], ligne['timestamp'], ligne['ts']]) {
      if (typeof candidat === 'number' && Number.isFinite(candidat)) {
        return candidat > 1_000_000_000_000 ? candidat : candidat * 1000;
      }
      if (typeof candidat === 'string' && candidat.trim().length > 0) {
        const ts = Date.parse(candidat);
        if (!Number.isNaN(ts)) return ts;
      }
    }
    return null;
  }

  private extraireValeurNumerique(ligne: LigneDonnees): number {
    for (const candidat of [ligne.valeur, ligne.value, ligne.mesure, ligne['y']]) {
      const valeur = Number(candidat);
      if (!Number.isNaN(valeur)) return valeur;
    }
    return 0;
  }

  private extraireValeurDepuisCle(ligne: LigneDonnees, cle: string, defaut: number): number {
    const valeur = Number(ligne[cle]);
    return Number.isNaN(valeur) ? defaut : valeur;
  }
}
